import { useCallback } from 'react';

import { useFreezeGrid } from '../contexts';

type Args = {
  fertilizationId: string;
  /** Embryo ids rendered by the calling table. */
  ownIds: string[];
  /** Embryo ids belonging to the same fert but rendered by the other table. */
  siblingIds: string[];
  /** Currently selected ids from the sibling table's selection list. */
  siblingSelectedIds: string[];
};

/**
 * Keeps the master fert row's selection in step with its embryo selections.
 *
 * A fert owns both a live and a temp embryo table, so neither table can decide
 * on its own whether the parent should stay selected — it has to account for
 * the sibling table too. Previously the grids only ever *selected* the parent,
 * which is why deselecting every embryo left the mating showing as selected
 * (WI 37941 #5).
 *
 * This intentionally only reacts to embryo toggles rather than deriving the
 * parent from selection state: a user can select a fert row directly without
 * selecting any embryos, and that must not immediately undo itself.
 */
export function useFertRowSelection({
  fertilizationId,
  ownIds,
  siblingIds,
  siblingSelectedIds,
}: Args) {
  const { gridApi } = useFreezeGrid();

  return useCallback(
    (nextOwnSelectedIds: string[]) => {
      if (!gridApi) return;
      const node = gridApi.getRowNode(fertilizationId);
      if (!node) return;

      const ownSet = new Set(ownIds);
      const siblingSet = new Set(siblingIds);
      const stillSelected =
        nextOwnSelectedIds.some((id) => ownSet.has(id)) ||
        siblingSelectedIds.some((id) => siblingSet.has(id));

      if (node.isSelected() !== stillSelected) {
        node.setSelected(stillSelected, false);
      }
    },
    [gridApi, fertilizationId, ownIds, siblingIds, siblingSelectedIds],
  );
}
